import React from 'react';
import { useListener } from 'react-bus';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';

import { Box, ColumnLayout, Container, Header, Loader, NavigateLink, StatusIndicator } from 'components';

import { DATE_TIME_FORMAT } from 'consts';
import {
    getRunError,
    getRunPriority,
    getRunProbeStatuses,
    getRunStatusMessage,
    getStatusIconColor,
    getStatusIconType,
} from 'libs/run';
import { ROUTES } from 'routes';
import { useGetRunQuery } from 'services/run';

import { finishedRunStatuses } from 'pages/Runs/constants';
import { runIsStopped } from 'pages/Runs/utils';

import {
    getRunListFinishedAt,
    getRunListItemBackend,
    getRunListItemInstanceId,
    getRunListItemPrice,
    getRunListItemRegion,
    getRunListItemResources,
    getRunListItemSchedule,
    getRunListItemSpotLabelKey,
} from '../../List/helpers';
import { RUN_DETAILS_REFRESH_LIST_EVENT } from '../constants';
import { EventsList } from '../Events/List';
import { JobList } from '../Jobs/List';
import { ConnectToRunWithDevEnvConfiguration } from './ConnectToRunWithDevEnvConfiguration';
import { ConnectToServiceRun } from './ConnectToServiceRun';
import { ConnectToTaskRun } from './ConnectToTaskRun';

export const RunDetails = () => {
    const { t } = useTranslation();
    const params = useParams();
    const paramProjectName = params.projectName ?? '';
    const paramRunId = params.runId ?? '';

    const {
        data: runData,
        isLoading: isLoadingRun,
        refetch,
    } = useGetRunQuery({
        project_name: paramProjectName,
        id: paramRunId,
    });

    useListener(RUN_DETAILS_REFRESH_LIST_EVENT, refetch);

    const schedule = runData ? getRunListItemSchedule(runData) : null;
    const nextTriggeredAt = runData ? runData.next_triggered_at : null;

    if (isLoadingRun)
        return (
            <Container>
                <Loader />
            </Container>
        );

    if (!runData) return null;

    const status = finishedRunStatuses.includes(runData.status)
        ? (runData.latest_job_submission?.status ?? runData.status)
        : runData.status;

    const terminationReason = finishedRunStatuses.includes(runData.status)
        ? runData.latest_job_submission?.termination_reason
        : null;

    const finishedAt = getRunListFinishedAt(runData);

    const statusMessage = getRunStatusMessage(runData);

    const renderRobeStatuses = () => {
        const statuses = getRunProbeStatuses(runData);

        if (!statuses.length) return '-';

        return statuses.map((statusType, index) => <StatusIndicator key={index} type={statusType} />);
    };

    return (
        <>
            <Container header={<Header variant="h2">{t('common.general')}</Header>}>
                <ColumnLayout columns={4} variant="text-grid">
                    <div>
                        <Box variant="awsui-key-label">{t('projects.run.project')}</Box>

                        <div>
                            <NavigateLink href={ROUTES.PROJECT.DETAILS.FORMAT(runData.project_name)}>
                                {runData.project_name}
                            </NavigateLink>
                        </div>
                    </div>

                    <div>
                        <Box variant="awsui-key-label">{t('projects.run.configuration')}</Box>
                        <div>{runData.run_spec.configuration_path || '-'}</div>
                    </div>

                    <div>
                        <Box variant="awsui-key-label">{t('projects.run.resources')}</Box>
                        <div>{getRunListItemResources(runData)}</div>
                    </div>

                    <div>
                        <Box variant="awsui-key-label">{t('projects.run.status')}</Box>

                        <div>
                            <StatusIndicator
                                type={getStatusIconType(status, terminationReason)}
                                colorOverride={getStatusIconColor(status, terminationReason, statusMessage)}
                            >
                                {statusMessage}
                            </StatusIndicator>
                        </div>
                    </div>

                    {runData.jobs.length <= 1 && (
                        <div>
                            <Box variant="awsui-key-label">{t('projects.run.probe')}</Box>
                            <div>{renderRobeStatuses()}</div>
                        </div>
                    )}

                    <div>
                        <Box variant="awsui-key-label">{t('projects.run.hub_user_name')}</Box>

                        <div>
                            <NavigateLink href={ROUTES.USER.DETAILS.FORMAT(runData.user)}>{runData.user}</NavigateLink>
                        </div>
                    </div>

                    <div>
                        <Box variant="awsui-key-label">{t('projects.run.submitted_at')}</Box>
                        <div>{format(new Date(runData.submitted_at), DATE_TIME_FORMAT)}</div>
                    </div>

                    <div>
                        <Box variant="awsui-key-label">{t('projects.run.finished_at')}</Box>
                        <div>{finishedAt ? format(new Date(finishedAt), DATE_TIME_FORMAT) : '-'}</div>
                    </div>

                    <div>
                        <Box variant="awsui-key-label">{t('projects.run.error')}</Box>
                        <div>{getRunError(runData) ?? '-'}</div>
                    </div>

                    <div>
                        <Box variant="awsui-key-label">{t('projects.run.price')}</Box>
                        <div>{getRunListItemPrice(runData)}</div>
                    </div>

                    <div>
                        <Box variant="awsui-key-label">{t('projects.run.cost')}</Box>
                        <div>${runData.cost}</div>
                    </div>

                    <div>
                        <Box variant="awsui-key-label">{t('projects.run.spot')}</Box>
                        <div>{t(getRunListItemSpotLabelKey(runData))}</div>
                    </div>

                    <div>
                        <Box variant="awsui-key-label">{t('projects.run.backend')}</Box>
                        <div>{getRunListItemBackend(runData)}</div>
                    </div>

                    <div>
                        <Box variant="awsui-key-label">{t('projects.run.region')}</Box>
                        <div>{getRunListItemRegion(runData)}</div>
                    </div>

                    <div>
                        <Box variant="awsui-key-label">{t('projects.run.priority')}</Box>
                        <div>{getRunPriority(runData)}</div>
                    </div>

                    <div>
                        <Box variant="awsui-key-label">{t('projects.run.instance_id')}</Box>
                        <div>{getRunListItemInstanceId(runData)}</div>
                    </div>
                </ColumnLayout>

                {schedule && (
                    <ColumnLayout columns={4} variant="text-grid">
                        <div>
                            <Box variant="awsui-key-label">{t('projects.run.schedule')}</Box>
                            <div>{schedule}</div>
                        </div>
                        <div>
                            <Box variant="awsui-key-label">{t('projects.run.next_run')}</Box>
                            <div>{nextTriggeredAt ? format(new Date(nextTriggeredAt), DATE_TIME_FORMAT) : '-'}</div>
                        </div>
                    </ColumnLayout>
                )}
            </Container>

            {runData.run_spec.configuration.type === 'dev-environment' && !runIsStopped(runData.status) && (
                <ConnectToRunWithDevEnvConfiguration run={runData} />
            )}

            {runData.run_spec.configuration.type === 'service' && !runIsStopped(runData.status) && (
                <ConnectToServiceRun run={runData} />
            )}

            {runData.run_spec.configuration.type === 'task' &&
                !runIsStopped(runData.status) &&
                (runData.jobs[0]?.job_spec?.app_specs?.length ?? 0) > 0 && <ConnectToTaskRun run={runData} />}

            {runData.jobs.length > 1 && (
                <JobList
                    projectName={paramProjectName}
                    runId={paramRunId}
                    jobs={runData.jobs}
                    runPriority={getRunPriority(runData)}
                />
            )}

            {runData.jobs.length > 1 && <EventsList />}
        </>
    );
};
